import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Phone, UserCircle, Users, Rocket, Check, X, MessageCircle, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { PaymentButton } from './PaymentButton';

const packages = [
  {
    id: 'discovery',
    tier: 'PACKAGE 01',
    icon: Phone,
    title: 'Discovery Call',
    subtitle: 'With Atishay Sobti',
    description: 'Africa Business Partner, 10 Years On-Ground Experience',
    price: '₹ 3,499',
    duration: 'Single Session | 45–60 Minutes',
    tagline: 'This is the starting point. Designed for entrepreneurs, investors, and business owners who have heard about Africa\'s opportunity and want a candid, informed conversation before committing any time or capital.',
    features: [
      'A candid assessment of whether your Africa idea, sector, or plan has genuine merit',
      'Clarity on which African country or region best fits your specific business or investment goal',
      'An honest understanding of the real challenges (regulatory, cultural, financial, operational)',
      'Sector-specific insights based on Atishay\'s direct experience',
      'An initial view on your first 3–5 action steps if you decide to move forward',
      'Guidance on what questions to ask and what to watch out for to avoid early mistakes',
      'A recommended next step (independent research, deeper engagement, or a higher-level conversation with Kirit Sobti)'
    ],
    excluded: [
      'A business plan, market research report, or written strategy document',
      'Follow-up calls or additional sessions beyond the single engagement',
      'Introduction to Kirit Sobti, government bodies, or Africa-based partners',
      'Any written notes, call summary, or action plan from Atishay\'s side',
      'Representation, negotiation, or any action taken on behalf of the client'
    ],
    notes: [
      'Format: Video call via Google Meet or WhatsApp',
      'Scheduling: Scheduled on Saturday or Sunday; confirmed within 48 hours of payment',
      'Preparation: Client is requested to fill out the sector questionnaire before the call',
      'Language: Hindi or English, as preferred by the client',
      'Not recorded - this is a confidential advisory conversation'
    ],
    advisorNote: {
      name: 'Atishay Sobti',
      message: 'I have spent 10 years navigating Africa - the opportunities, the setbacks and everything in between. In this call, I will give you the same honest perspective I would give a close friend or family member exploring Africa for the first time. No sales pitch. Just real insight so you can make a properly informed decision.'
    },
    color: '#007AFF',
    featured: false
  },
  {
    id: 'senior-discovery',
    tier: 'PACKAGE 02',
    icon: UserCircle,
    title: 'Senior Discovery Call',
    subtitle: 'With Kirit Sobti',
    description: '30 Years Africa Business Experience',
    price: '₹ 7,500',
    duration: 'Single Session | 60 Minutes',
    tagline: 'For those who want to speak directly with Kirit Sobti at the very start - without going through a preliminary round. This is a one-time, senior-level conversation with one of India\'s most experienced Africa business minds.',
    features: [
      'Kirit Sobti\'s direct, unfiltered personal perspective on your Africa idea, plan, or business question',
      'A frank assessment of whether your target country, sector, and timing make strategic sense',
      'Insight from 30 years of on-ground Africa experience',
      'Clarity on the most important decisions you need to make and common expensive mistakes to avoid',
      'A high-level direction: Is the idea worth pursuing? What needs to change? What should your next 90 days look like?',
      'Preliminary guidance on the right type of engagement, structure, or partners if you decide to move forward'
    ],
    excluded: [
      'Multiple calls, ongoing access, or follow-up sessions',
      'Written strategy documents, reports, or business plans',
      'Introductions to African government officials, partners, or embassy contacts',
      'Any representation or action on behalf of the client',
      'Commitment to any further engagement (this is a standalone session)'
    ],
    notes: [
      'Format: Video call or in-person in Delhi NCR (subject to mutual availability and confirmation)',
      'Scheduling: Scheduled within 5–7 working days of payment confirmation, subject to Kirit Sobti\'s calendar',
      'Preparation: Client is requested to share a brief written overview of their idea or question in advance',
      'Language: Hindi or English, as preferred by the client',
      'Not recorded - confidential advisory conversation'
    ],
    advisorNote: {
      name: 'Kirit Sobti',
      message: 'Africa has given me more than I could have imagined - but it has also tested me in ways I did not expect. In this call, I will tell you what I genuinely think about your Africa plan. I will not tell you what you want to hear. I will tell you what will actually help you - because that is the only kind of advice worth paying for.'
    },
    color: '#9B59B6',
    featured: false
  },
  {
    id: 'strategic-advisory',
    tier: 'PACKAGE 03',
    icon: Users,
    title: 'Strategic Advisory',
    subtitle: 'With Kirit Sobti',
    description: '6 Month Engagement',
    price: '₹ 1,00,000',
    duration: '6 Months | Up to 8 Sessions',
    tagline: 'This is for those who are serious about Africa and want a structured, high-quality advisory relationship over six months. Not just a call or two - but a real strategic engagement designed to give you a clear, actionable Africa entry plan, backed by Kirit Sobti\'s network, perspective and 30 years of hard-earned judgment.',
    features: [
      'Africa Entry Strategy Session — country selection, entry model, sector landscape analysis',
      'Regulatory & Business Setup Orientation — company registration, banking, tax, compliance overview',
      'Stakeholder & Network Guidance — government, trade bodies, embassy approach, partner vetting',
      'Risk Assessment & Due Diligence Briefing — country, sector, currency, political, operational risks',
      'Clear 90-Day Action Roadmap — priorities, professionals to engage, key milestones'
    ],
    excluded: [
      'Legal representation, company incorporation, banking setup, or compliance filings',
      'Guaranteed introductions (network sharing is at Kirit Sobti\'s discretion)',
      'Travel by Kirit Sobti outside Delhi NCR or in Africa',
      'Execution of any business task, negotiation, or operational activity on behalf of the client',
      'Any financial return guarantee, business success warranty, or outcome commitment',
      'Drafting of legal documents, agreements, MoUs, or contracts',
      'Sessions beyond 8 within the 6-month period without additional engagement'
    ],
    notes: [
      'Format: Up to 8 calls or meetings with Kirit Sobti over 6 months (video call, phone, or in-person in Delhi NCR)',
      'Scheduling: Mutually agreed, sessions scheduled in advance',
      'Response Time: Non-emergency questions addressed in the next session or by brief note',
      'Duration: 6 months from the date of confirmed payment and onboarding',
      'Language: Hindi or English, as preferred by the client',
      'Note: Kirit Sobti provides advisory orientation - not legal representation. Client must engage a qualified Africa-based legal professional for filings and formal compliance.',
      'Expiry: Unused sessions do not carry over or result in a refund after 6 months'
    ],
    color: '#D4AF37',
    featured: true,
    badge: 'MOST CHOSEN'
  },
  {
    id: 'full-handholding',
    tier: 'PACKAGE 04',
    icon: Rocket,
    title: 'Full Handholding',
    subtitle: 'With Kirit Sobti',
    description: '6 to 8 Month Deep Engagement',
    price: '₹ 3,00,000',
    duration: '6–8 Months | Up to 20 Sessions + Active Support',
    tagline: 'The most comprehensive engagement available. This is for those who are not just planning - they are ready to build. The Full Handholding package is a deep, sustained advisory relationship where Kirit Sobti walks alongside you at every critical stage of your Africa journey.',
    features: [
      'Complete Africa Business Entry Strategy — deep-dive, phased plan, business model stress-testing',
      'Regulatory Navigation & Setup Roadmap — company formation, banking, incentives, local hiring guidance',
      'Stakeholder Access & Introductions — personal Africa network access, embassy guidance, meeting preparation',
      'Africa Travel & Ground Visit Planning — pre-visit briefing, joint visits possible (client bears all travel costs)',
      'Risk Management & Contingency Planning — detailed risk register, protection strategies, exit options',
      'Sourcing, Supply Chain & Vendor Guidance — logistics, freight, customs, quality control',
      'End-of-Engagement Review & Forward Plan — 6-month forward action plan with milestones'
    ],
    excluded: [
      'Legal representation, contract drafting, company registration, or compliance filings',
      'Any financial investment, co-investment, loan, or funding arrangement',
      'Guaranteed business outcomes, revenue projections, license approvals, or partnership agreements',
      'All travel, accommodation, local transport, and living expenses of Kirit Sobti (fully borne by client)',
      'Representation in negotiations, government meetings, or legal proceedings on behalf of the client',
      'Introductions not within Kirit Sobti\'s existing and active network',
      'Work beyond 20 sessions without a separate extension agreement'
    ],
    notes: [
      'Format: Up to 20 structured sessions with Kirit Sobti over 6 to 8 months (video call, phone, or in-person in Delhi NCR)',
      'Between Sessions: Key written questions may be sent and addressed in the next session or by brief written note',
      'Scheduling: Managed through the team, minimum 5 working days\' advance notice for each session',
      'Travel: Joint Africa travel is possible if mutually agreed. All costs of Kirit Sobti (flights, 5-star accommodation, local transport, per diem) are fully borne by the client',
      'Duration: 6 to 8 months from confirmed payment; specific end date agreed at onboarding',
      'Language: Hindi or English, as preferred by the client',
      'Note: Introductions are made at Kirit Sobti\'s discretion and are not guaranteed',
      'Expiry: Unused sessions lapse at engagement end - no refund or carry-over'
    ],
    advisorNote: {
      name: 'Kirit Sobti',
      message: 'In 30 years in Africa, the businesses that succeeded were not always the ones with the most money. They were the ones that moved with the right knowledge, built the right relationships and stayed the course when it got difficult. That is exactly what I will help you do. I do not take on many engagements at this level - because I take each one seriously. If you are in this, so am I.'
    },
    color: '#50C878',
    featured: false
  },
];

const generalDisclaimer = {
  title: 'General Terms & Conditions — Applicable to All Packages',
  items: [
    'Nature of Advisory: All engagements are advisory. Kirit and Atishay Sobti share experience, perspective, and guidance. They do not provide legal, financial, tax, or accounting advice. Clients must engage qualified professionals for these matters in both India and Africa.',
    'No Guarantee of Outcomes: No engagement, deliverable, or introduction guarantees business success, revenue, government approval, license grant, investment return, or partnership formation. Outcomes depend entirely on the client\'s execution, capital commitment, and market conditions.',
    'Payment Policy: Full payment is required in advance to confirm any engagement. Sessions are not scheduled until payment is confirmed. All fees are non-refundable once the first session has been conducted. For packages 3 and 4, if no session has been conducted within 30 days of payment, a full refund may be requested.',
    'Confidentiality: All information shared by the client is treated as strictly confidential. Clients agree to treat all shared insights, network information, and strategic guidance as confidential.',
    'Cancellation & Rescheduling: Sessions must be rescheduled at least 48 hours in advance. Sessions cancelled with less than 48 hours\' notice will be treated as utilized. Advisors may reschedule with reasonable advance notice.',
    'Introductions & Third Parties: Introductions are made at the advisor\'s discretion and only where a genuine fit exists. The advisor is not responsible for the conduct, reliability, financial standing, or outcomes of any introduced third party.',
    'Travel: Travel by Kirit Sobti or Atishay Sobti (within India or to Africa) is not included in any package fee. If travel is agreed upon, all costs (flights, 5-star accommodation, local transport, visa, per diem) are fully borne by the client.',
    'Disputes: Disputes will be resolved through direct, good-faith dialogue. All engagements are governed by the laws of India. Formal disputes are subject to the jurisdiction of courts in New Delhi.'
  ]
};

export const PackagesSection = () => {
  const [expandedPackage, setExpandedPackage] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const togglePackage = (id) => {
    setExpandedPackage(expandedPackage === id ? null : id);
  };

  return (
    <section 
      id="packages" 
      className="section bg-[#080808] relative overflow-hidden"
      data-testid="packages-section"
    >
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#D4AF37] opacity-[0.03] rounded-full blur-[200px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-6"
        >
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">Advisory Packages</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            You Have the Ambition.
            <br />
            <span className="gradient-text">I Have the Map.</span>
          </h2>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.2 }}
          className="text-center text-white/60 max-w-2xl mx-auto mb-12"
        >
          Whether you're exploring Africa for the first time, need a senior perspective on your plan, or are ready for deep handholding — there's a path for you here.
        </motion.p>

        {/* Packages Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1 }}
              className={`relative rounded-2xl p-5 flex flex-col ${
                pkg.featured 
                  ? 'bg-gradient-to-b from-[#D4AF37]/10 to-transparent border-2 border-[#D4AF37]/40' 
                  : 'glass-light border border-white/10'
              }`}
              data-testid={`package-${pkg.id}`}
            >
              {pkg.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-bold rounded-full tracking-wider">
                  {pkg.badge}
                </div>
              )}
              
              {/* Tier Label */}
              <p className="text-[10px] tracking-[0.2em] text-[#E4405F] mb-2">{pkg.tier}</p>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-white font-['Playfair_Display'] mb-1">{pkg.title}</h3>
              
              {/* Subtitle */}
              <p className="text-sm text-[#D4AF37] mb-1">{pkg.subtitle}</p>
              <p className="text-xs text-white/50 mb-3">{pkg.description}</p>
              
              {/* Price */}
              <div className="mb-3 pb-3 border-b border-white/10">
                <p className="text-2xl font-bold text-white">{pkg.price}</p>
                <p className="text-xs text-white/40">{pkg.duration}</p>
              </div>
              
              {/* Key Features (First 3) */}
              <ul className="space-y-2 flex-1 mb-4">
                {pkg.features.slice(0, 3).map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check size={12} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                    <span className="text-xs text-white/70 leading-relaxed">{feature}</span>
                  </li>
                ))}
                {pkg.features.length > 3 && (
                  <li className="text-xs text-white/40 pl-4">+ {pkg.features.length - 3} more...</li>
                )}
              </ul>
              
              {/* View Details Button */}
              <button
                onClick={() => togglePackage(pkg.id)}
                className="w-full py-2 mb-2 text-sm text-[#D4AF37] border border-[#D4AF37]/30 rounded-lg hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2"
              >
                View Full Details
                {expandedPackage === pkg.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              
              {/* Payment Button */}
              <PaymentButton
                packageId={pkg.id}
                packageName={pkg.title}
                amount={pkg.price}
                className="w-full"
              />
            </motion.div>
          ))}
        </div>

        {/* Expanded Package Details Modal */}
        <AnimatePresence>
          {expandedPackage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
              onClick={() => setExpandedPackage(null)}
            >
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative w-full max-w-2xl glass rounded-2xl p-6 my-8 max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {(() => {
                  const pkg = packages.find(p => p.id === expandedPackage);
                  if (!pkg) return null;
                  
                  return (
                    <>
                      <button
                        onClick={() => setExpandedPackage(null)}
                        className="absolute top-4 right-4 text-white/60 hover:text-white"
                      >
                        <X size={24} />
                      </button>

                      <p className="text-xs tracking-[0.2em] text-[#E4405F] mb-2">{pkg.tier}</p>
                      <h3 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-1">{pkg.title}</h3>
                      <p className="text-[#D4AF37] mb-1">{pkg.subtitle}</p>
                      <p className="text-sm text-white/50 mb-4">{pkg.description}</p>
                      
                      {/* Tagline */}
                      {pkg.tagline && (
                        <p className="text-sm text-white/70 italic mb-4 pl-3 border-l-2 border-[#D4AF37]/50">{pkg.tagline}</p>
                      )}
                      
                      <div className="flex items-baseline gap-3 mb-6 pb-4 border-b border-white/10">
                        <p className="text-3xl font-bold text-white">{pkg.price}</p>
                        <p className="text-sm text-white/40">{pkg.duration}</p>
                      </div>

                      {/* All Features */}
                      <div className="mb-6">
                        <p className="text-sm font-semibold text-white mb-3">What's Included</p>
                        <ul className="space-y-3">
                          {pkg.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Check size={14} className="text-[#50C878] mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-white/70">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Excluded */}
                      <div className="mb-6 p-4 bg-white/5 rounded-xl">
                        <p className="text-xs text-[#E4405F] font-bold tracking-wider mb-3">EXCLUDED</p>
                        <ul className="space-y-2">
                          {pkg.excluded.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <X size={12} className="text-[#E4405F] mt-0.5 flex-shrink-0" />
                              <span className="text-xs text-white/50">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Notes */}
                      <div className="mb-6">
                        <p className="text-sm font-semibold text-white mb-3">Important Notes</p>
                        <ul className="space-y-2">
                          {pkg.notes.map((note, idx) => (
                            <li key={idx} className="text-xs text-white/50">• {note}</li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Advisor Note */}
                      {pkg.advisorNote && (
                        <div className="mb-6 p-4 bg-[#D4AF37]/10 rounded-xl border border-[#D4AF37]/30">
                          <p className="text-xs text-[#D4AF37] font-bold tracking-wider mb-2">NOTE FROM {pkg.advisorNote.name.toUpperCase()}</p>
                          <p className="text-sm text-white/80 italic leading-relaxed">"{pkg.advisorNote.message}"</p>
                        </div>
                      )}

                      <div className="space-y-3">
                        <PaymentButton
                          packageId={pkg.id}
                          packageName={pkg.title}
                          amount={pkg.price}
                          className="w-full"
                          onSuccess={() => setExpandedPackage(null)}
                        />
                        <button
                          onClick={() => {
                            setExpandedPackage(null);
                            scrollToContact();
                          }}
                          className="btn-secondary w-full text-sm"
                        >
                          Have Questions? Contact Us
                        </button>
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Discovery Call Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.7 }}
          className="bg-[#0f1419] border-l-4 border-[#D4AF37] rounded-r-xl p-6 mb-8"
        >
          <p className="text-base text-white/80 italic font-['Playfair_Display']">
            All packages begin with the <span className="text-[#D4AF37] font-semibold not-italic">Discovery Call</span> with Atishay Sobti — this is the first step for every serious prospect before moving forward.
          </p>
        </motion.div>

        {/* General Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="glass-light rounded-xl p-4 mb-8"
        >
          <button
            onClick={() => setShowDisclaimer(!showDisclaimer)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-[#F39C12]" />
              <span className="text-sm font-medium text-white">{generalDisclaimer.title}</span>
            </div>
            {showDisclaimer ? <ChevronUp size={16} className="text-white/50" /> : <ChevronDown size={16} className="text-white/50" />}
          </button>
          
          <AnimatePresence>
            {showDisclaimer && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <ul className="mt-4 space-y-2 pt-4 border-t border-white/10">
                  {generalDisclaimer.items.map((item, idx) => (
                    <li key={idx} className="text-xs text-white/50">• {item}</li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9 }}
          className="text-center"
        >
          <p className="text-white/60 mb-6">
            Not sure which package is right for you? Start with a Discovery Call — it's the fastest way to get clarity.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={scrollToContact}
              className="btn-primary flex items-center gap-2"
              data-testid="book-discovery-btn"
            >
              Book a Discovery Call
            </button>
            <a
              href="https://wa.me/919899969196"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2"
              data-testid="whatsapp-btn"
            >
              <MessageCircle size={18} />
              Chat on WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
